import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Table } from 'react-bootstrap';
import { Section, PostFilterResponse, UpdateSectionRequest } from '../../Models/Models';
import { SiteContext } from '../../Contexts';
import { PaginationControl } from 'react-bootstrap-pagination-control';
import { EntityColors } from '../../Themes/EntityColors';
import { Utils } from '../../Services/Utils';
import { SectionService } from '../../Services/SectionService';
import { PostService } from '../../Services/PostService';
import DiscardButton from '../Buttons/DiscardButton';
import SaveButton from '../Buttons/SaveButton';
import FormShell from '../Form/FormShell';
import { useIsDirty } from '../../Hooks/useIsDirty';

const SectionEditComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Sekcie');
    }, []);

    const { filterPosts } = PostService();
    const { getSection, updateSection } = SectionService();
    const perPage = 6;
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.text;
    const { sectionId } = useParams<{ sectionId: string }>();

    const [currentPage, setCurrentPage] = useState(1);
    const [section, setSection] = useState<Section | null>(null);
    const [posts, setPosts] = useState<PostFilterResponse | null>(null);
    const [selectedPosts, setSelectedPosts] = useState<number[]>([]);
    const [loaded, setLoaded] = useState(false);
    const isDirty = useIsDirty(selectedPosts, loaded);

    const loadPosts = async (pageNum: number) => {
        const resp = await filterPosts({
            websiteId: currentSite.id,
            limit: perPage,
            offset: (pageNum - 1) * perPage,
        });
        setPosts(resp);
    };

    useEffect(() => {
        const load = async () => {
            const resp = await getSection(Number(sectionId));
            setSection(resp);
            setSelectedPosts(resp.posts.map(c => c.id).filter(id => id != null));
            await loadPosts(1);
            setLoaded(true);
        };
        void load();
    }, [sectionId, currentSite.id]);

    const togglePost = (postId: number, checked: boolean) => {
        setSelectedPosts(prev => checked ? [...prev, postId] : prev.filter(id => id !== postId));
    };

    const doSave = async () => {
        const payload: UpdateSectionRequest = {
            sectionId: section.id,
            postIds: selectedPosts.filter((id): id is number => id != null),
        };
        await updateSection(payload);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        void doSave();
    };

    return (
        <FormShell title="Upraviť sekciu" icon={icon} accentColor={action} backHref="/page/table" isDirty={isDirty}>
            {!loaded ? (
                <p className="text-center py-4 text-secondary">Načítavam...</p>
            ) : (
                <form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold fs-5 mb-3 d-block">Priradené príspevky</Form.Label>
                        {!posts ? (
                            <p className="text-center text-muted">Načítavam príspevky...</p>
                        ) : (
                            <div style={{
                                border: '1px solid #E2E8F0',
                                borderRadius: '10px',
                                overflow: 'hidden',
                            }}>
                                <Table hover responsive className="align-middle mb-0">
                                    <thead style={{ backgroundColor: '#F8FAFC', borderBottom: `2px solid ${action}` }}>
                                        <tr>
                                            <th className="text-center" style={{ width: '3rem' }}>Vybrať</th>
                                            <th>Nadpis</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {posts.data.map(cat => {
                                            const selected = selectedPosts.includes(cat.id);
                                            return (
                                                <tr
                                                    key={cat.id}
                                                    style={{ backgroundColor: selected ? action + '0D' : 'inherit' }}
                                                >
                                                    <td className="text-center">
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={e => togglePost(cat.id, e.target.checked)}
                                                            style={{
                                                                width: '20px',
                                                                height: '20px',
                                                                accentColor: action,
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="fw-medium">{cat.headline}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>

                                <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #F1F5F9' }}>
                                    <PaginationControl
                                        page={currentPage}
                                        between={4}
                                        total={posts.totalCount}
                                        limit={perPage}
                                        changePage={p => { setCurrentPage(p); loadPosts(p); }}
                                        ellipsis={1}
                                    />
                                </div>
                            </div>
                        )}
                    </Form.Group>

                    <div className="d-flex justify-content-center mt-2 gap-3">
                        <DiscardButton to="/page/table" isDirty={isDirty} />
                        <SaveButton type="submit" onSave={doSave} websiteId={currentSite.id} />
                    </div>
                </form>
            )}
        </FormShell>
    );
};

export default SectionEditComponent;
